import { Component } from 'react';
import { withTranslation } from 'react-i18next';
import moment from 'moment';
import Loading from '../../../../components/Loading';
import { getAvailablePromptCredits } from '../../../../helpers/aiTrackingPromptCredits';
import SettingsCard from '../SettingsCard';
import { getProjectTopics, getPromptCredits } from '../query';
import GeneratePromptsModal from './GeneratePromptsModal';

const COLLAPSED_COUNT = 8;

/**
 * Read-only history of every topic generated for (or hand-added to) this project.
 *
 * Topics are produced by the prompt generator, not here, so this card mostly reports what
 * exists and links back to that flow. The one thing it can do itself is fill a gap: a topic
 * that ended up with no prompts offers a Generate action on hover, which runs the generator
 * for that topic alone. Long histories collapse to the most recent few because a mature
 * project can accumulate hundreds of rows.
 *
 * Both of those spend prompt generation credits, so the card carries the same remaining-credit
 * note the prompt generator puts under its own Generate button, and refreshes it after a run.
 */
class Topics extends Component {
    constructor() {
        super();

        this.onToggleShowAll = this.onToggleShowAll.bind(this);
        this.onCloseGenerateModal = this.onCloseGenerateModal.bind(this);
        this.onPromptsGenerated = this.onPromptsGenerated.bind(this);

        this.state = {
            loading: true,
            topics: [],
            showAll: false,
            error: '',
            generateTopic: null,
            creditsLoading: true,
            // null while unknown, so a count that never arrived can be left out entirely.
            promptCredits: null,
        };
    }

    componentDidMount() {
        this.getTopics();
        this.getCredits();
    }

    async getTopics() {
        try {
            const res = await getProjectTopics(this.props.projectId);
            const data = await res.json();

            if (res.status !== 200) {
                this.setState({
                    loading: false,
                    error: data?.detail ?? this.props.t('global.something_went_wrong'),
                });
                return;
            }

            this.setState({ loading: false, topics: Array.isArray(data) ? data : [] });
        } catch (err) {
            this.setState({
                loading: false,
                error: err?.message ?? this.props.t('global.something_went_wrong'),
            });
        }
    }

    /**
     * How many prompt generations the user has left, for the note under the generate action.
     *
     * A count that failed to load is dropped rather than shown as zero: zero is a real state
     * with a real meaning -- nothing left to generate -- and claiming it off a failed request
     * would tell the user something untrue about their plan.
     */
    async getCredits() {
        try {
            const res = await getPromptCredits();
            const data = await res.json();

            this.setState({
                creditsLoading: false,
                promptCredits: res.status === 200 ? getAvailablePromptCredits(data) : null,
            });
        } catch (err) {
            this.setState({ creditsLoading: false, promptCredits: null });
        }
    }

    onToggleShowAll() {
        this.setState((prevState) => ({ showAll: !prevState.showAll }));
    }

    onOpenGenerateModal(topic) {
        this.setState({ generateTopic: topic });
    }

    onCloseGenerateModal() {
        this.setState({ generateTopic: null });
    }

    // The row that was generated for now has prompts, so its count and its Generate action
    // are both out of date -- and the run just spent credits, so the note is too.
    onPromptsGenerated() {
        this.getTopics();
        this.getCredits();
    }

    render() {
        const { t, projectId } = this.props;
        const { loading, topics, showAll, creditsLoading, promptCredits } = this.state;

        const visible = showAll ? topics : topics.slice(0, COLLAPSED_COUNT);

        const actions = (
            <div>
                <a
                    href={`/ai-tracking/prompt-generator/?view=brand_to_entities_to_prompts&projectId=${projectId}`}
                    className="button button-outline button-primary width-sm btn"
                >
                    {t('ai_tracking.projects.settings.topics.generate')}
                </a>
                {creditsLoading && (
                    <div className="prompt-generation-count">
                        <Loading onlyPreloader />
                    </div>
                )}
                {!creditsLoading && promptCredits !== null && (
                    <div className="prompt-generation-count">
                        {promptCredits} {t('ai_tracking.global.prompts_generation_left')}
                    </div>
                )}
            </div>
        );

        return (
            <SettingsCard
                title={t('ai_tracking.projects.settings.topics.header')}
                subtitle={t('ai_tracking.projects.settings.topics.subtitle')}
                actions={actions}
            >
                {loading && <Loading padding={10} />}
                {!loading && !!this.state.error && (
                    <div className="competitors-error-message my-10">
                        <img src="/static/img/alert-icon.svg" alt="icon" />
                        {this.state.error}
                    </div>
                )}
                {!loading && !this.state.error && topics.length === 0 && (
                    <div className="settings-empty">
                        <i className="icon-ai-keyword settings-empty-icon" />
                        <div className="settings-empty-title">
                            {t('ai_tracking.projects.settings.topics.empty_title')}
                        </div>
                        <div className="settings-empty-text">
                            {t('ai_tracking.projects.settings.topics.empty_text')}
                        </div>
                    </div>
                )}
                {!loading && topics.length > 0 && (
                    <>
                        <div className="settings-topics">
                            {visible.map((topic) => {
                                const promptCount = topic.generated_prompts?.length ?? 0;

                                return (
                                    <div className="settings-topic" key={topic.id}>
                                        <div className="settings-topic-main">
                                            <div className="settings-topic-name">{topic.name}</div>
                                            {!!topic.description && (
                                                <div className="settings-topic-description">
                                                    {topic.description}
                                                </div>
                                            )}
                                        </div>
                                        <div className="settings-topic-meta">
                                            {topic.is_user_added && (
                                                <span className="settings-topic-badge">
                                                    {t(
                                                        'ai_tracking.projects.settings.topics.user_added',
                                                    )}
                                                </span>
                                            )}
                                            {promptCount === 0 && (
                                                <button
                                                    type="button"
                                                    className="settings-topic-generate"
                                                    onClick={() => this.onOpenGenerateModal(topic)}
                                                >
                                                    {t(
                                                        'ai_tracking.prompt_generator.generate_prompts',
                                                    )}
                                                </button>
                                            )}
                                            <span>
                                                {t('ai_tracking.projects.settings.topics.prompts', {
                                                    count: promptCount,
                                                })}
                                            </span>
                                            <span>
                                                {moment(topic.created_at).format('MMM D, YYYY')}
                                            </span>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        {topics.length > COLLAPSED_COUNT && (
                            <button
                                type="button"
                                className="settings-topics-toggle"
                                onClick={this.onToggleShowAll}
                            >
                                {showAll
                                    ? t('ai_tracking.projects.settings.topics.show_less')
                                    : t('ai_tracking.projects.settings.topics.show_all', {
                                          count: topics.length,
                                      })}
                            </button>
                        )}
                    </>
                )}
                {!!this.state.generateTopic && (
                    <GeneratePromptsModal
                        isOpen
                        topic={this.state.generateTopic}
                        project={this.props.project}
                        onClose={this.onCloseGenerateModal}
                        onGenerated={this.onPromptsGenerated}
                    />
                )}
            </SettingsCard>
        );
    }
}

export default withTranslation()(Topics);
